import type { Block } from '@pageboss/shared';
import { InspectorSection } from './inspector-section';
import { InspectorField } from './inspector-field';
import { SegmentedControl } from './segmented-control';
import { Repeater, RepeaterTitle } from './repeater';
import { StringListEditor } from './string-list-editor';
import { Toggle } from './toggle';

type PricingBlock = Extract<Block, { type: 'pricing' }>;
type PricingProps = PricingBlock['props'];
type Tier = PricingProps['tiers'][number];

const COLUMN_OPTIONS = [
  { value: 2 as const, label: '2' },
  { value: 3 as const, label: '3' },
];

function newTier(): Tier {
  return {
    name: 'New tier',
    price: '$0',
    period: '',
    description: '',
    features: ['Feature one'],
    cta: { label: 'Get started', href: '' },
    highlighted: false,
  };
}

interface PricingInspectorProps {
  block: PricingBlock;
  onChange: (next: PricingProps) => void;
}

export function PricingInspector({ block, onChange }: PricingInspectorProps) {
  const props = block.props;
  const patch = (next: Partial<PricingProps>) => onChange({ ...props, ...next });

  return (
    <>
      <InspectorSection>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">Columns</span>
          <SegmentedControl
            ariaLabel="Columns"
            value={props.columns}
            options={COLUMN_OPTIONS}
            onChange={(v) => patch({ columns: v })}
          />
          <p className="text-xs text-text-tertiary mt-0.5">
            Edit title and subtitle directly on the canvas.
          </p>
        </div>
      </InspectorSection>

      <InspectorSection label="Tiers">
        <Repeater
          items={props.tiers}
          onChange={(tiers) => patch({ tiers: tiers as PricingProps['tiers'] })}
          addLabel="Add tier"
          makeNew={newTier}
          renderTitle={(tier) => (
            <span className="inline-flex items-center gap-2">
              <RepeaterTitle>{tier.name}</RepeaterTitle>
              {tier.highlighted ? (
                <span className="rounded-full bg-brand-50 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-brand-700">
                  Featured
                </span>
              ) : null}
            </span>
          )}
          renderForm={(tier, _i, update) => (
            <>
              <InspectorField
                label="Name"
                value={tier.name}
                placeholder="Essential"
                onChange={(v) => update({ name: v })}
              />
              <div className="grid grid-cols-2 gap-3">
                <InspectorField
                  label="Price"
                  value={tier.price}
                  placeholder="$120"
                  onChange={(v) => update({ price: v })}
                />
                <InspectorField
                  label="Period (optional)"
                  value={tier.period ?? ''}
                  placeholder="/ project"
                  onChange={(v) => update({ period: v })}
                />
              </div>
              <InspectorField
                label="Description (optional)"
                value={tier.description ?? ''}
                multiline
                rows={2}
                placeholder="Best for owners just getting started"
                onChange={(v) => update({ description: v })}
              />
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground">Features</span>
                <StringListEditor
                  values={tier.features}
                  onChange={(features) => update({ features })}
                  addLabel="Add feature"
                  placeholder="Up to 2 hours of work"
                  newItem="New feature"
                />
              </div>
              <div className="rounded-[var(--radius-sm)] bg-surface-sunken p-3 flex flex-col gap-3">
                <span className="eyebrow">
                  Button
                </span>
                <InspectorField
                  label="Label"
                  value={tier.cta.label}
                  placeholder="Book this package"
                  onChange={(v) => update({ cta: { ...tier.cta, label: v } })}
                />
                <InspectorField
                  label="Link (optional)"
                  type="url"
                  value={tier.cta.href}
                  placeholder="https://... or #contact"
                  onChange={(v) => update({ cta: { ...tier.cta, href: v } })}
                />
              </div>
              <Toggle
                label="Highlight this tier"
                description="Adds a ring + Featured badge"
                checked={tier.highlighted}
                onChange={(checked) => update({ highlighted: checked })}
              />
            </>
          )}
        />
      </InspectorSection>
    </>
  );
}
